import React, { useEffect, useState } from 'react';
import api from '../api';
import { useTheme } from '../contexts/ThemeContext';

interface Sablon {
  tur: string;
  ad: string;
  konu: string;
  icerik: string;
  degiskenler: string;
  aktif: boolean;
  guncellendi: string | null;
}

const TUR_IKONU: Record<string, string> = {
  kredi_yuklendi:      '₺',
  ders_hakki_alindi:   '🎵',
  hak_bitti:           '⚠️',
  son_hak_uyarisi:     '⏰',
  dogum_gunu_ogrenci:  '🎂',
  dogum_gunu_egitmen:  '🎂',
  hakedis_odendi:      '💰',
  ders_atama:          '📋',
};

function tarihFormat(s: string | null) {
  if (!s) return 'Hiç düzenlenmedi';
  const d = new Date(s);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

export default function OtomatikPostalar() {
  const { isDark } = useTheme();
  const card      = isDark ? '#1a1a1a' : 'white';
  const cardAlt   = isDark ? '#161b22' : '#F8FAFC';
  const border    = isDark ? '#2a2a2a' : '#E5E7EB';
  const borderSub = isDark ? '#202020' : '#F3F4F6';
  const textPrimary = isDark ? '#e8e8e8' : '#111827';
  const textSec   = isDark ? '#999' : '#6B7280';
  const textMuted = isDark ? '#666' : '#9CA3AF';

  const [sablonlar, setSablonlar] = useState<Sablon[]>([]);
  const [secili, setSecili] = useState<Sablon | null>(null);
  const [form, setForm] = useState({ konu: '', icerik: '', aktif: true });
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [bilgi, setBilgi] = useState<{ ok: boolean; mesaj: string } | null>(null);

  useEffect(() => {
    api.get('/api/email-sablonlar')
      .then(r => { setSablonlar(r.data); if (r.data.length) seciliAta(r.data[0]); })
      .catch(() => {})
      .finally(() => setYukleniyor(false));
  }, []); // eslint-disable-line

  function seciliAta(s: Sablon) {
    setSecili(s);
    setForm({ konu: s.konu, icerik: s.icerik, aktif: s.aktif });
    setBilgi(null);
  }

  async function kaydet() {
    if (!secili) return;
    setKaydediliyor(true);
    setBilgi(null);
    try {
      await api.put(`/api/email-sablonlar/${secili.tur}`, form);
      setSablonlar(prev => prev.map(s => s.tur === secili.tur ? { ...s, ...form, guncellendi: new Date().toISOString() } : s));
      setSecili(prev => prev ? { ...prev, ...form, guncellendi: new Date().toISOString() } : prev);
      setBilgi({ ok: true, mesaj: 'Şablon kaydedildi.' });
    } catch {
      setBilgi({ ok: false, mesaj: 'Kaydedilemedi. Lütfen tekrar deneyin.' });
    } finally {
      setKaydediliyor(false);
    }
  }

  if (yukleniyor) return (
    <div style={{ padding: '24px 28px', color: textMuted, fontSize: 14 }}>Yükleniyor...</div>
  );

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100 }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: textPrimary, margin: 0 }}>Otomatik Postalar</h1>
        <div style={{ fontSize: 13, color: textMuted, marginTop: 4 }}>
          Mailgun ile gönderilen otomatik e-posta metinleri
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* Sol: şablon listesi */}
        <div style={{ width: 240, flexShrink: 0, background: card, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
          {sablonlar.map(s => (
            <button
              key={s.tur}
              onClick={() => seciliAta(s)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '12px 14px', border: 'none', borderBottom: `1px solid ${borderSub}`,
                background: secili?.tur === s.tur ? (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE') : card,
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 18, width: 24, textAlign: 'center', flexShrink: 0 }}>
                {TUR_IKONU[s.tur] ?? '✉'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: secili?.tur === s.tur ? '#3C3489' : textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.ad}
                </div>
                <div style={{ marginTop: 3 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20,
                    background: s.aktif ? (isDark ? 'rgba(16,185,129,0.15)' : '#D1FAE5') : (isDark ? '#2a2a2a' : '#F3F4F6'),
                    color: s.aktif ? '#065F46' : textMuted,
                  }}>
                    {s.aktif ? 'Aktif' : 'Kapalı'}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Sağ: düzenleme formu */}
        {secili && (
          <div style={{ flex: 1, background: card, border: `1px solid ${border}`, borderRadius: 12, padding: '24px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <span style={{ fontSize: 24 }}>{TUR_IKONU[secili.tur] ?? '✉'}</span>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: textPrimary }}>{secili.ad}</div>
                <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>Son değişiklik: {tarihFormat(secili.guncellendi)}</div>
              </div>
            </div>

            {/* Değişkenler */}
            <div style={{ background: isDark ? '#161b22' : '#F8FAFC', border: `1px solid ${isDark ? '#2a2a2a' : '#E2E8F0'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#888' : '#64748B', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Kullanılabilir değişkenler
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {secili.degiskenler?.split(',').map(v => v.trim()).filter(Boolean).map(v => (
                  <code key={v} style={{ fontSize: 12, background: isDark ? 'rgba(83,74,183,0.15)' : '#EEF2FF', color: isDark ? '#9d98e8' : '#3730A3', padding: '2px 8px', borderRadius: 4, fontFamily: 'monospace' }}>
                    {v}
                  </code>
                ))}
              </div>
            </div>

            {/* Konu */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: isDark ? '#bbb' : '#374151', marginBottom: 6 }}>
                E-posta Konusu
              </label>
              <input
                value={form.konu}
                onChange={e => setForm(p => ({ ...p, konu: e.target.value }))}
                style={{
                  width: '100%', padding: '9px 12px', border: `1px solid ${border}`, borderRadius: 8,
                  fontSize: 14, boxSizing: 'border-box',
                  outline: 'none', fontFamily: 'sans-serif',
                }}
              />
            </div>

            {/* İçerik */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: isDark ? '#bbb' : '#374151', marginBottom: 6 }}>
                Mesaj İçeriği
              </label>
              <textarea
                value={form.icerik}
                onChange={e => setForm(p => ({ ...p, icerik: e.target.value }))}
                rows={6}
                style={{
                  width: '100%', padding: '10px 12px', border: `1px solid ${border}`, borderRadius: 8,
                  fontSize: 14, boxSizing: 'border-box', resize: 'vertical',
                  outline: 'none', fontFamily: 'sans-serif', lineHeight: 1.6,
                }}
              />
              <div style={{ fontSize: 11, color: textMuted, marginTop: 4 }}>
                Dinamik veriler (tutar tablosu, program vb.) e-postada otomatik eklenir.
              </div>
            </div>

            {/* Aktif toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
              <input
                type="checkbox"
                id="aktif"
                checked={form.aktif}
                onChange={e => setForm(p => ({ ...p, aktif: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#3C3489' }}
              />
              <label htmlFor="aktif" style={{ fontSize: 13, color: isDark ? '#ccc' : '#374151', cursor: 'pointer', userSelect: 'none' }}>
                Bu e-posta türü aktif (gönderilsin)
              </label>
            </div>

            {/* Bilgi mesajı */}
            {bilgi && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
                background: bilgi.ok ? (isDark ? 'rgba(16,185,129,0.1)' : '#F0FDF4') : (isDark ? 'rgba(228,75,74,0.1)' : '#FFF1F2'),
                color: bilgi.ok ? '#15803D' : '#BE123C',
                border: `1px solid ${bilgi.ok ? (isDark ? 'rgba(16,185,129,0.3)' : '#BBF7D0') : (isDark ? 'rgba(228,75,74,0.3)' : '#FECDD3')}`,
              }}>
                {bilgi.ok ? '✓ ' : '✗ '}{bilgi.mesaj}
              </div>
            )}

            {/* Kaydet */}
            <button
              onClick={kaydet}
              disabled={kaydediliyor}
              style={{
                padding: '10px 24px', background: kaydediliyor ? (isDark ? '#555' : '#9CA3AF') : '#3C3489',
                color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                cursor: kaydediliyor ? 'default' : 'pointer',
              }}
            >
              {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
